/**
 * Continuous multi-shot camera — same UX as Walk capture (shutter + checkmark).
 * Used for After photos (and any freeform burst gallery).
 * In-app camera only — no gallery upload; capture metadata stored for claims.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Camera, Check, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import {
  WALK_MIN_PHOTOS,
  collectSimplePhotoUrls,
  readSimpleFlowState,
  type SimpleFlowState,
} from "@/lib/simpleTurnover";
import {
  appendPhotoCaptureRecord,
  captureFrameFromVideo,
  uploadInAppCleaningPhoto,
} from "@/lib/cleaningPhotoCapture";
import { simpleFlowSegmentIndex, type TurnoverWizardStepId } from "@/lib/turnoverWizardPlan";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";

type Mode = "camera" | "review";
type Phase = "before" | "after";

interface Props {
  phase: Phase;
  propertyId: string;
  taskId: string;
  entryWalkthroughState: unknown;
  onComplete: () => void;
  title?: string;
  hint?: string;
  /** Primary CTA on review (default Next). */
  doneLabel?: string;
  minPhotos?: number;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
}

export function SimpleBurstPhotoStep({
  phase,
  propertyId,
  taskId,
  entryWalkthroughState,
  onComplete,
  title,
  hint,
  doneLabel,
  minPhotos = WALK_MIN_PHOTOS,
  onBack,
  onSelectSegment,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = false,
}: Props) {
  const { t } = useTranslation();
  const initial = readSimpleFlowState(entryWalkthroughState);
  const initialUrls = collectSimplePhotoUrls(initial, phase);

  const [mode, setMode] = useState<Mode>(() => (initialUrls.length >= minPhotos ? "review" : "camera"));
  const [urls, setUrls] = useState<string[]>(initialUrls);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const shutterLock = useRef(false);

  const stepId: TurnoverWizardStepId = phase === "before" ? "before_photos" : "after_photos";
  const displayTitle =
    title === "Before" || title === "After" || title == null
      ? t(phase === "before" ? "beforeTitle" : "afterTitle")
      : title;
  const displayHint =
    hint === "Photo everything" || hint === "Photo everything clean" || hint == null
      ? t(phase === "before" ? "photoEverything" : "photoEverythingClean")
      : hint;
  const ctaLabel = doneLabel == null || doneLabel === "Next" ? t("next") : doneLabel;
  const canFinish = urls.length >= minPhotos;
  const photoCountLabel =
    urls.length < minPhotos && mode === "review"
      ? t("photoProgress").replace("{{count}}", String(urls.length)).replace("{{min}}", String(minPhotos))
      : urls.length === 1
        ? t("photoCountOne").replace("{{count}}", String(urls.length))
        : t("photoCount").replace("{{count}}", String(urls.length));
  const strip = useMemo(() => urls.slice(-4), [urls]);

  const persist = useCallback(
    async (patch: Partial<SimpleFlowState>) => {
      const prev = readSimpleFlowState(entryWalkthroughState);
      const merged: SimpleFlowState = { ...prev, simple_flow: true, ...patch };
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({ entry_walkthrough_state: merged })
        .eq("id", taskId);
      if (error) throw error;
    },
    [entryWalkthroughState, taskId],
  );

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("cameraUnavailableOpen"));
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;
        await video.play().catch(() => undefined);
      }
      setCameraReady(true);
    } catch (err) {
      console.error("[SimpleBurstPhotoStep] camera", err);
      const name = err instanceof Error ? err.name : "";
      setCameraError(
        name === "NotAllowedError"
          ? t("allowCameraKeepShooting")
          : t("couldNotOpenCameraTap"),
      );
      setCameraReady(false);
    }
  }, [stopCamera]);

  useEffect(() => {
    if (mode === "camera") {
      void startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [mode, startCamera, stopCamera]);

  const appendPhoto = async (blob: Blob, width: number, height: number) => {
    const moment = phase === "before" ? "walk" : "after";
    const { url, record } = await uploadInAppCleaningPhoto({
      propertyId,
      taskId,
      moment,
      blob,
      width,
      height,
      storagePath: `${propertyId}/${taskId}/simple-${phase}/burst/${crypto.randomUUID()}.jpg`,
    });
    const nextUrls = [...urls, url];
    setUrls(nextUrls);
    setFlash(true);
    window.setTimeout(() => setFlash(false), 280);
    const prev = readSimpleFlowState(entryWalkthroughState);
    const patch: Partial<SimpleFlowState> = {
      photo_captures: appendPhotoCaptureRecord(prev.photo_captures, record),
    };
    if (phase === "before") patch.before_photo_urls = nextUrls;
    else patch.after_photo_urls = nextUrls;
    await persist(patch);
    return url;
  };

  const captureFromVideo = async () => {
    if (shutterLock.current || busy) return;
    const video = videoRef.current;
    if (!video || !cameraReady) {
      toast({
        title: t("cameraRequired"),
        description: t("cameraRequiredDesc"),
        variant: "destructive",
      });
      void startCamera();
      return;
    }
    shutterLock.current = true;
    setBusy(true);
    try {
      const frame = await captureFrameFromVideo(video);
      await appendPhoto(frame.blob, frame.width, frame.height);
    } catch (err) {
      toast({
        title: t("captureFailed"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      shutterLock.current = false;
    }
  };

  const removeAt = async (index: number) => {
    const nextUrls = urls.filter((_, i) => i !== index);
    setUrls(nextUrls);
    try {
      if (phase === "before") await persist({ before_photo_urls: nextUrls });
      else await persist({ after_photo_urls: nextUrls });
    } catch {
      /* keep local */
    }
  };

  const finish = async () => {
    if (!canFinish) {
      toast({
        title: t("needNPhotos").replace("{{min}}", String(minPhotos)),
        description: displayHint,
      });
      return;
    }
    setBusy(true);
    try {
      if (phase === "before") {
        await persist({ before_photo_urls: urls, before_complete: true });
        await supabase.from("cleaning_tasks").update({ entry_walkthrough_completed: true }).eq("id", taskId);
      } else {
        await persist({ after_photo_urls: urls });
        await supabase.from("cleaning_tasks").update({ checklist_completed: true }).eq("id", taskId);
      }
      stopCamera();
      onComplete();
    } catch (err) {
      toast({
        title: t("couldNotContinue"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn(CLEANER_SIMPLE_STEP_SHELL, "relative text-[#0f1720]")}>
      <SimpleStepHeader
        title={displayTitle}
        activeSegment={simpleFlowSegmentIndex(stepId, { includeMaintenance, includeQuickCheck, includeGuestSetup })}
        includeMaintenance={includeMaintenance}
        includeQuickCheck={includeQuickCheck}
        includeGuestSetup={includeGuestSetup}
        onBack={onBack}
        onSelectSegment={onSelectSegment}
        right={
          <div
            className={cn(
              "text-[15px] font-bold",
              canFinish
                ? "text-[#24a89a]"
                : mode === "review" && urls.length > 0
                  ? "text-[#b45309]"
                  : "text-[#94a3b8]",
            )}
          >
            {photoCountLabel}
          </div>
        }
        subtitle={<div className="text-[17px] font-medium text-[#64748b]">{displayHint}</div>}
      />

      {mode === "camera" ? (
        <div className="flex min-h-0 flex-1 flex-col px-5 pb-4">
          <div className="relative flex min-h-0 flex-1 flex-col justify-end overflow-hidden rounded-[20px] bg-[#1c2732]">
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className={cn(
                "absolute inset-0 h-full w-full object-cover",
                !cameraReady && "opacity-0",
              )}
            />
            {!cameraReady ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                {busy ? (
                  <Loader2 className="h-8 w-8 animate-spin text-white" />
                ) : (
                  <>
                    <Camera className="h-10 w-10 text-white/50" strokeWidth={1.8} />
                    <p className="text-[15px] font-medium text-white/70">
                      {cameraError || t("startingCamera")}
                    </p>
                    <button
                      type="button"
                      onClick={() => void startCamera()}
                      className="mt-1 rounded-full bg-[#24a89a] px-5 py-3 text-[15px] font-bold text-white"
                    >
                      {t("openCamera")}
                    </button>
                    <p className="text-[12px] font-medium text-white/45">
                      {t("photosInAppOnly")}
                    </p>
                  </>
                )}
              </div>
            ) : null}

            {cameraReady ? (
              <div className="pointer-events-none absolute left-1/2 top-[46%] h-[130px] w-[130px] -translate-x-1/2 -translate-y-1/2 rounded-[22px] border-2 border-white/20" />
            ) : null}

            {urls.length > 0 ? (
              <div className="absolute right-4 top-4 rounded-full bg-[#24a89a] px-3 py-1.5 text-[13px] font-bold text-white">
                {urls.length === 1 ? t("photoCountOne").replace("{{count}}", String(urls.length)) : t("photoCount").replace("{{count}}", String(urls.length))}
              </div>
            ) : null}

            {flash ? (
              <div className="pointer-events-none absolute inset-0 animate-[burstFlash_0.28s_ease-out] bg-white" />
            ) : null}

            {strip.length > 0 ? (
              <div className="relative z-[1] flex items-center gap-2 px-3.5 pb-3.5">
                {strip.map((url, i) => {
                  const last = i === strip.length - 1;
                  return (
                    <div
                      key={url}
                      className={cn(
                        "relative shrink-0 overflow-hidden rounded-xl border",
                        last
                          ? "h-[62px] w-[62px] border-2 border-[#24a89a]"
                          : "h-12 w-12 border-[1.5px] border-white/25",
                      )}
                    >
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    </div>
                  );
                })}
              </div>
            ) : null}

            <div className="relative z-[1] flex items-center justify-between px-[18px] pb-[22px]">
              {/* Spacer — mirrors Walk Issue slot so shutter stays centered */}
              <div className="h-[60px] w-[60px]" aria-hidden />

              <button
                type="button"
                disabled={busy}
                onClick={() => void captureFromVideo()}
                aria-label={t("capture")}
                className="flex h-[92px] w-[92px] items-center justify-center rounded-full border-[5px] border-white/55 active:scale-95 disabled:opacity-60"
              >
                <span className="flex h-[70px] w-[70px] items-center justify-center rounded-full bg-white">
                  {busy ? <Loader2 className="h-7 w-7 animate-spin text-[#24a89a]" /> : null}
                </span>
              </button>

              <button
                type="button"
                disabled={urls.length === 0}
                onClick={() => setMode("review")}
                aria-label={t("doneShooting")}
                className={cn(
                  "flex h-[60px] w-[60px] items-center justify-center rounded-[18px] active:scale-95",
                  urls.length > 0
                    ? "bg-[#24a89a] text-white"
                    : "border-[1.5px] border-white/14 bg-white/6 text-white/35",
                )}
              >
                <Check className="h-6 w-6" strokeWidth={2.8} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-5">
            <button
              type="button"
              onClick={() => setMode("camera")}
              className="mb-3 flex h-[68px] shrink-0 items-center justify-center gap-2.5 rounded-[18px] border-[1.5px] border-[#24a89a] bg-[#e2eeed] active:scale-[0.99]"
            >
              <Camera className="h-6 w-6 text-[#15776d]" strokeWidth={2.3} />
              <span className="text-[19px] font-bold text-[#15776d]">{t("keepShooting")}</span>
            </button>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2 [-webkit-overflow-scrolling:touch]">
              <div className="grid grid-cols-3 gap-2">
                {urls.map((url, i) => (
                  <div
                    key={url}
                    className="relative aspect-square overflow-hidden rounded-[13px] border-[1.5px] border-[#e6edf1] bg-[#eff4f7]"
                  >
                    <img src={url} alt="" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => void removeAt(i)}
                      className="absolute right-1.5 top-1.5 flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[rgba(15,23,32,0.6)] text-white"
                      aria-label={t("remove")}
                    >
                      <X className="h-[11px] w-[11px]" strokeWidth={3.4} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={cn(CLEANER_SIMPLE_CTA_FOOTER, "border-t border-[#e6edf1]/60 bg-white")}>
            <button
              type="button"
              disabled={busy || !canFinish}
              onClick={() => void finish()}
              className={cn(
                "flex h-[72px] w-full items-center justify-center rounded-[18px] text-[23px] font-bold",
                canFinish
                  ? "bg-[#24a89a] text-white hover:bg-[#1f958a] active:bg-[#1b8378]"
                  : "bg-[#eaf0f3] text-[#94a3b8]",
              )}
            >
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : ctaLabel}
            </button>
          </div>
        </>
      )}

      <style>{`@keyframes burstFlash { from { opacity: 0.85; } to { opacity: 0; } }`}</style>
    </div>
  );
}
